import * as React from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";

import { INCOMPLETE_ACCENT, type MissingRequirement, type RequiredFieldState } from "../jobRequirements";

/**
 * The inline note under a required field that was left empty at submit time.
 *
 * Requiredness is deliberately not marked at rest — no asterisk, no label. The
 * amber outline and this note appear together only once the user has asked to
 * create the job, so the form never flags an omission before the user has had a
 * chance to make it. The note carries the meaning in words for anyone who cannot
 * distinguish amber from grey.
 */
export const RequiredFieldNote: React.FC<{ state: RequiredFieldState; children: React.ReactNode }> = ({ state, children }) => {
  return state.highlight ? <div style={{ marginTop: "4px", fontSize: "12px", color: INCOMPLETE_ACCENT.text }}>{children}</div> : null;
};

/**
 * The list of everything still outstanding, shown next to the Create Job button
 * after a submit attempt.
 *
 * One summary scales better than decorating every field: the user reads what is
 * left in one place instead of hunting the form for coloured outlines, and screen
 * readers announce it as a single alert.
 */
export const MissingRequirementsSummary = React.forwardRef<HTMLDivElement, { missing: MissingRequirement[] }>(({ missing }, ref) => {
  if (missing.length === 0) return null;

  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        marginBottom: "12px",
        padding: "10px 12px",
        borderRadius: "6px",
        border: `1px solid ${INCOMPLETE_ACCENT.border}`,
        borderLeft: `4px solid ${INCOMPLETE_ACCENT.border}`,
        backgroundColor: INCOMPLETE_ACCENT.background,
        color: INCOMPLETE_ACCENT.text,
        fontSize: "0.9em",
      }}
    >
      <SvgIcon icon={svgIcons.infoCircleIcon} style={{ marginTop: "2px", flexShrink: 0 }} />
      <div>
        <strong>Still needed before this job can run:</strong>
        <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
          {missing.map((item) => (
            <li key={item.key}>{item.label}</li>
          ))}
        </ul>
      </div>
    </div>
  );
});

MissingRequirementsSummary.displayName = "MissingRequirementsSummary";
